import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { Tooltip } from "../tooltip";
import { InlineError, InlineSpinner, useInlineEdit } from "./shared";

/**
 * Free-text prose, edited in place — the opportunity's note.
 *
 * The keyboard contract differs from every other field in the kit, deliberately: **Enter is
 * a newline**, because this is prose and a note that could not hold a second line would be
 * a single-line field wearing a textarea. Commit is on blur; Escape still cancels and writes
 * nothing.
 *
 * A trimmed-empty draft commits `null` — "there is no note" — rather than an empty string,
 * so clearing a note returns the "+ Add note" affordance instead of leaving an empty box.
 */
export function InlineTextarea({
  label,
  value,
  editable,
  disabled,
  placeholder,
  /**
   * Mount already open. Was for the retired "+ Add note" button; kept for a caller that opens a
   * field in response to something other than a click on the field itself.
   */
  startEditing = false,
  validate,
  onCommit,
}: {
  label: string;
  value: string | null;
  editable: boolean;
  disabled?: boolean;
  placeholder?: string;
  startEditing?: boolean;
  /** Refuse a value locally — the message is shown under the field and nothing is sent. */
  validate?: (next: string | null) => string | undefined;
  onCommit: (next: string | null) => Promise<unknown>;
}) {
  const t = useT();
  const field = useInlineEdit<string | null>({
    value,
    equals: (a, b) => normalise(a) === normalise(b),
    onCommit: (next) => onCommit(normalise(next)),
    validate: validate ? (next) => validate(normalise(next)) : undefined,
    initiallyEditing: startEditing,
  });

  if (!field.editing) {
    if (!editable) {
      return <p className="text-sm whitespace-pre-wrap">{value}</p>;
    }
    return (
      // Same hint as every other inline field, through the same shared tooltip.
      <Tooltip content={disabled ? t("common.paused") : t("inline.clickToEdit", label)}>
        <button
          type="button"
          disabled={disabled || field.saving}
          onClick={field.begin}
          className="hover:bg-foreground/10 focus-visible:ring-ring -mx-1 flex w-full cursor-pointer items-start gap-1.5 rounded px-1 text-left text-sm whitespace-pre-wrap focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default disabled:hover:bg-transparent"
        >
          {/*
            An empty field shows its placeholder *inside* the button, which is the affordance the
            reader clicks.

            It is also the button's accessible name: with a null value this control had no name
            at all, so a screen reader announced "button" and nothing else, and
            `getByRole("button", { name })` could not find it either. That is why the empty state
            pairs a short visible placeholder — a dash, since 2026-08-26, because a sentence of
            invitation was the longest line in the hero and said nothing a reader needed — with
            the field's own label read out instead of it. Visually a dash; audibly "Notes".
          */}
          <span className={cn("min-w-0 flex-1", value === null && "text-muted-foreground")}>
            {value ?? (
              <>
                <span aria-hidden>{placeholder ?? label}</span>
                <span className="sr-only">{label}</span>
              </>
            )}
          </span>
          {field.saving && <InlineSpinner />}
        </button>
      </Tooltip>
    );
  }

  return (
    <div>
      <div className="flex items-start gap-1.5">
        <textarea
          aria-label={label}
          value={field.draft ?? ""}
          rows={3}
          placeholder={placeholder}
          disabled={field.saving}
          onChange={(event) => field.setDraft(event.target.value)}
          onBlur={() => void field.commit(field.draft)}
          onKeyDown={(event) => {
            // Enter deliberately falls through to the textarea: it is a newline here.
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            field.cancel();
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="border-input bg-background focus-visible:ring-ring w-full rounded border px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60"
        />
        {field.saving && <InlineSpinner />}
      </div>
      <p className="text-muted-foreground mt-0.5 text-xs">{t("inline.textareaHint")}</p>
      {field.error && <InlineError message={field.error} />}
    </div>
  );
}

/** Whitespace is the same thing as nothing typed — the column never holds `""`. */
function normalise(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
